import { Currency } from '../currency';
import { isPresent } from '../typingUtils';

export type MonetaryValue = {
    amount: string;
    currency: Currency;
};

export function extractAmountFromMonetaryValue(
    monetaryValue: MonetaryValue | null | undefined,
    options?: { ifAbsent?: number },
): number {
    if (!isPresent(monetaryValue)) {
        return options?.ifAbsent ?? 0;
    }

    return Number(monetaryValue?.amount);
}

/** For backend responses that send `{ [currency]: amount }` instead of a MonetaryValue. */
export function convertIntoMonetaryValue(value: Record<string, string> | null | undefined): MonetaryValue | null {
    if (!isPresent(value)) {
        return null;
    }

    const entries = Object.entries(value);
    if (entries.length === 0) {
        return null;
    }

    const [currency, amount] = entries[0];

    return {
        amount,
        currency: currency as Currency,
    };
}

export function isMonetaryValue(value: unknown): value is MonetaryValue {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const { amount, currency } = value as MonetaryValue;

    if (typeof amount !== 'string' || amount.trim() === '') {
        return false;
    }

    if (!isValidNumber(amount)) {
        return false;
    }

    if (!Object.values(Currency).includes(currency)) {
        return false;
    }

    return true;
}

function isValidNumber(value: string): boolean {
    return !isNaN(parseFloat(value)) && isFinite(Number(value));
}
